import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import {
  listPastOrders, pastOrdersSummary,
  getOrderByUid, getOrderBySheetId,
  syncNew, resyncByUid, listCustomerReturns, listWriteoffs,
} from '../api/orderHistory';
import { getRtoFeedbackAudioBlob } from '../api/riderAssignment';
import { lookupCoins } from '../api/skipzyAdmin';
import { useAuth } from '../context/AuthContext';
import { useSizes } from '../api/meta';
import Modal from '../components/Modal';

// ── Constants ─────────────────────────────────────────────────────────────

const PRESETS = [
  { key: 'today',    label: 'Today' },
  { key: '7days',    label: 'Last 7 days' },
  { key: 'week',     label: 'This week' },
  { key: 'month',    label: 'This month' },
  { key: 'lifetime', label: 'Lifetime' },
  { key: 'custom',   label: 'Custom' },
];

const STATUSES = [
  { key: 'completed', label: 'COMPLETED' },
  { key: 'rto',       label: 'RTO' },
  { key: 'dol',       label: 'DOL' },
];

const ALL_COLUMNS = [
  { key: 'date',             label: 'Date',              default: true  },
  { key: 'customer',         label: 'Customer',          default: true  },
  { key: 'area',             label: 'Area / Pincode',    default: false },
  { key: 'children',         label: 'Children',          default: false },
  { key: 'executive',        label: 'Executive',         default: true  },
  { key: 'source',           label: 'Source',            default: true  },
  { key: 'status',           label: 'Status',            default: true  },
  { key: 'items_dispatched', label: 'Items dispatched',  default: false },
  { key: 'avg_piece_age',    label: 'Avg piece age',     default: false },
  { key: 'pack_rating',      label: 'Pack rating',       default: true  },
  { key: 'items',            label: 'Items sold',        default: true  },
  { key: 'brands_sold',      label: 'Brands sold',       default: true  },
  { key: 'pre_discount',     label: 'Pre-discount',      default: false },
  { key: 'gmv',              label: 'GMV',               default: true  },
  { key: 'revenue',          label: 'Revenue',           default: false },
  { key: 'discount_pct',     label: 'Discount %',        default: true  },
  { key: 'discount_code',    label: 'Discount code',     default: false },
  { key: 'payment',          label: 'Payment',           default: false },
  { key: 'margin',           label: 'Margin (% GMV)',    default: true, adminOnly: true },
];

const COLUMNS_STORAGE_KEY = 'zocs_past_orders_columns_v3';

// ── Helpers ───────────────────────────────────────────────────────────────

function computeDateRange(presetKey, customFrom, customTo) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
  if (presetKey === 'today') return { from: today, to: endOfToday };
  if (presetKey === '7days') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from, to: endOfToday };
  }
  if (presetKey === 'week') {
    const from = new Date(today);
    const day = from.getDay();
    const diff = day === 0 ? 6 : day - 1;
    from.setDate(from.getDate() - diff);
    return { from, to: endOfToday };
  }
  if (presetKey === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from, to: endOfToday };
  }
  if (presetKey === 'lifetime') return { from: null, to: null };
  if (presetKey === 'custom') {
    const from = customFrom ? new Date(customFrom + 'T00:00:00') : null;
    const to   = customTo   ? new Date(customTo + 'T23:59:59')   : null;
    return { from, to };
  }
  return { from: null, to: null };
}

function fmtYMD(d) {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtINR(amount) {
  if (amount == null) return '—';
  return '₹' + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function fmtMarginPct(margin, gmv) {
  if (margin == null || gmv == null || Number(gmv) === 0) return '';
  const pct = (Number(margin) / Number(gmv)) * 100;
  return `(${pct.toFixed(1)}% of GMV)`;
}

function fmtMarginWithPct(margin, gmv) {
  if (margin == null) return '—';
  const pct = fmtMarginPct(margin, gmv);
  return pct ? `${fmtINR(margin)} ${pct}` : fmtINR(margin);
}

function fmtNumber(n, digits = 0) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN', {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
}

function fmtDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw.endsWith('Z') ? raw : raw + 'Z');
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

// ── Sub-components ───────────────────────────────────────────────────────

// Why an RTO happened, as the rider reported it (or core team entered it on RTO Records).
// The recording is private on the rider side, so it is fetched only when play is pressed.
function RtoVoiceNote({ appointmentUid }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState('idle');
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const load = async () => {
    setState('loading');
    try {
      const blob = await getRtoFeedbackAudioBlob(appointmentUid);
      setUrl(URL.createObjectURL(blob));
      setState('ready');
    } catch {
      setState('error');
    }
  };
  if (state === 'ready') return <audio className="mt-2" controls autoPlay src={url} />;
  return (
    <div className="mt-2">
      <button
        className="text-xs font-semibold text-zoddle-blue hover:underline disabled:opacity-50"
        disabled={state === 'loading'}
        onClick={load}
      >
        {state === 'loading' ? 'Loading…' : '▶ Play voice note'}
      </button>
      {state === 'error' && <span className="text-xs text-zoddle-pink ml-2">Voice note unavailable</span>}
    </div>
  );
}

function RtoReason({ feedback, appointmentUid }) {
  if (!feedback) return <span className="text-gray-400">—</span>;
  const flags = [
    feedback.price_issue && 'Price',
    feedback.design_issue && 'Design',
    feedback.size_issue && 'Size',
  ].filter(Boolean);
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {flags.map(f => (
          <span key={f} className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">{f}</span>
        ))}
      </div>
      {feedback.comments && <div className="text-xs text-gray-600 mt-1 max-w-xs">{feedback.comments}</div>}
      {feedback.has_voice_note && appointmentUid && <RtoVoiceNote appointmentUid={appointmentUid} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: 'bg-green-100 text-green-700',
    rto:       'bg-red-100 text-red-700',
    dol:       'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status ? status.toUpperCase() : '—'}
    </span>
  );
}

function PackRating({ value }) {
  if (value == null) return <span className="text-gray-300">—</span>;
  return (
    <span className="whitespace-nowrap" title={`Packer rated this pack ${value}/5`}>
      <span className="text-yellow-400">{'★'.repeat(value)}</span>
      <span className="text-gray-200">{'★'.repeat(5 - value)}</span>
    </span>
  );
}

function CategoryBadge({ category }) {
  if (!category) return null;
  const map = {
    post_zocs:          { label: 'ZOCS',   cls: 'bg-zoddle-green/20 text-zoddle-green' },
    pre_zocs_with_uid:  { label: 'Pre-Z',  cls: 'bg-amber-100 text-amber-700' },
    pre_zocs_no_uid:    { label: 'Legacy', cls: 'bg-gray-100 text-gray-600' },
    rto:                { label: 'RTO',    cls: 'bg-red-100 text-red-700' },
    dol:                { label: 'DOL',    cls: 'bg-red-100 text-red-700' },
  };
  const e = map[category] || { label: category, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xxs font-medium px-1.5 py-0.5 rounded ${e.cls}`}>{e.label}</span>;
}

function StatBlock({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-gray-800 mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SummaryStrip({ summary, loading, isAdmin }) {
  if (loading) {
    return <div className="bg-white border border-gray-200 rounded p-4 text-sm text-gray-500">Loading summary…</div>;
  }
  if (!summary) return null;
  const marginPct = summary.gmv > 0 ? (summary.gross_margin / summary.gmv) * 100 : null;
  const cols = isAdmin ? 7 : 6;
  return (
    <div
      className={`bg-white border border-gray-200 rounded p-4 grid grid-cols-2 gap-4`}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      <StatBlock label="Orders"      value={fmtNumber(summary.order_count)} />
      <StatBlock label="Items sold"  value={fmtNumber(summary.item_count)} />
      <StatBlock label="Avg order"   value={fmtINR(summary.aov)} />
      <StatBlock label="Avg pieces"  value={fmtNumber(summary.avg_items_per_order, 1)} />
      <StatBlock label="GMV"         value={fmtINR(summary.gmv)} />
      <StatBlock label="Revenue"     value={fmtINR(summary.revenue)} />
      {isAdmin && (
        <StatBlock
          label="Gross margin"
          value={fmtINR(summary.gross_margin)}
          sub={marginPct != null ? `${marginPct.toFixed(1)}% of GMV` : null}
        />
      )}
    </div>
  );
}

function DetailModal({ order, onClose, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const fetcher = order.appointment_uid
      ? getOrderByUid(order.appointment_uid)
      : getOrderBySheetId(order.sheet_order_id);
    fetcher
      .then(d => { if (alive) { setData(d); setError(null); } })
      .catch(err => {
        if (alive) setError(err.response?.data?.detail || err.message || 'Failed to load');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [order]);

  // Coins come from the customer app, fetched only when an order is opened.
  const [coins, setCoins] = useState(null); // null = loading, false = none found
  useEffect(() => {
    if (!isAdmin || !order.appointment_uid) { setCoins(false); return undefined; }
    let alive = true;
    setCoins(null);
    lookupCoins(order.appointment_uid)
      .then(r => {
        if (!alive) return;
        const rows = r.orders || [];
        setCoins(rows.length ? {
          earned: rows.reduce((s, o) => s + o.coins_earned, 0),
          deducted: rows.reduce((s, o) => s + o.coins_deducted, 0),
          redeemed: rows.reduce((s, o) => s + (o.coins_redeemed || 0), 0),
        } : false);
      })
      .catch(() => { if (alive) setCoins(false); });
    return () => { alive = false; };
  }, [order, isAdmin]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-800">Order detail</h2>
            {data && <StatusBadge status={data.status} />}
            {data && <CategoryBadge category={data.category} />}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-zoddle-pink text-2xl leading-none" aria-label="Close">×</button>
        </div>
        {loading && <div className="p-8 text-center text-gray-500">Loading…</div>}
        {error && <div className="p-8 text-center text-zoddle-pink">{error}</div>}
        {data && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Customer</h3>
                <div className="text-base font-semibold text-gray-800">{data.customer_name || '—'}</div>
                <div className="text-sm text-gray-600">
                  {data.customer_phone || '—'}
                  {data.appointment_uid && <span className="text-gray-500"> ({data.appointment_uid})</span>}
                </div>
                {data.customer_address && (
                  <div className="text-sm text-gray-600 mt-1">{data.customer_address}</div>
                )}
                <div className="text-sm text-gray-600 mt-1"><span className="text-gray-500">Executive: </span>{data.executive || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded p-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Order</h3>
                <div className="text-sm text-gray-700"><span className="text-gray-500">Sale date: </span>{fmtDate(data.sale_date)}</div>
                <div className="text-sm text-gray-700"><span className="text-gray-500">Appointment UID: </span>{data.appointment_uid || <span className="text-gray-400">—</span>}</div>
                <div className="text-sm text-gray-700"><span className="text-gray-500">Pack rating: </span><PackRating value={data.pack_rating} /></div>
                <div className="text-sm text-gray-700"><span className="text-gray-500">Sheet Order ID: </span>{data.sheet_order_id}</div>
                <div className="text-sm text-gray-700"><span className="text-gray-500">Last synced: </span>{fmtDate(data.synced_at)}</div>
              </div>
            </div>

            {data.status === 'rto' && (
              <div className="bg-amber-50 rounded p-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Why nothing was bought</h3>
                <RtoReason feedback={data.rto_feedback} appointmentUid={data.appointment_uid} />
              </div>
            )}

            <div className="bg-gray-50 rounded p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-gray-500 uppercase">GMV</div><div className="font-semibold text-gray-800">{fmtINR(data.gmv)}</div></div>
              <div><div className="text-xs text-gray-500 uppercase">Revenue</div><div className="font-semibold text-gray-800">{fmtINR(data.revenue)}</div></div>
              <div><div className="text-xs text-gray-500 uppercase">Cost (pre-tax)</div><div className="font-semibold text-gray-800">{fmtINR(data.cost_pretax)}</div></div>
              {isAdmin && <div><div className="text-xs text-gray-500 uppercase">Gross margin</div><div className="font-semibold text-gray-800">{fmtMarginWithPct(data.gross_margin, data.gmv)}</div></div>}
            </div>

            {isAdmin && (
              <div className="bg-amber-50 rounded p-4 grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-gray-500 uppercase">🪙 Coins earned</div><div className="font-semibold text-gray-800">{coins === null ? '…' : coins ? coins.earned : '—'}</div></div>
                <div><div className="text-xs text-gray-500 uppercase">Coins spent</div><div className="font-semibold text-gray-800">{coins === null ? '…' : coins ? coins.redeemed : '—'}</div></div>
                <div><div className="text-xs text-gray-500 uppercase">Taken back</div><div className="font-semibold text-gray-800">{coins === null ? '…' : coins ? coins.deducted : '—'}</div></div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Line items ({data.line_items.length})</h3>
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-600">
                  <tr>
                    <th className="px-2 py-1 text-left">Image</th>
                    <th className="px-2 py-1 text-left">Barcode</th>
                    <th className="px-2 py-1 text-left">Design</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Sold (GMV)</th>
                    <th className="px-2 py-1 text-right">Taxable</th>
                    <th className="px-2 py-1 text-right">Cost</th>
                    {isAdmin && <th className="px-2 py-1 text-right">Margin</th>}
                    <th className="px-2 py-1 text-right">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="px-2 py-1">
                        {li.image_url ? (
                          <a href={li.image_url} target="_blank" rel="noopener noreferrer" title="Open full image">
                            <img
                              src={cldThumb(li.image_url, 300)}
                              alt={li.design_name || li.zoddle_barcode || ''}
                              className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80"
                              loading="lazy"
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          </a>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-700">{li.zoddle_barcode || '—'}</td>
                      <td className="px-2 py-1 text-gray-700">
                        {(li.design_name || li.returned) ? (
                          <>
                            <div>
                              {li.design_name || <span className="text-gray-300">—</span>}
                              {li.on_sale && (
                                <span
                                  title={`Sold on sale · Skipzy ${fmtINR(li.saver_price)} vs ${fmtINR(li.zoddle_price)}`}
                                  className="ml-1.5 align-middle text-xxs font-semibold px-1.5 py-0.5 rounded bg-zoddle-pink/20 text-zoddle-pink"
                                >
                                  SALE
                                </span>
                              )}
                              {li.returned && (
                                <span
                                  title={`Returned — ${li.return_reason || 'no reason given'}${li.refund_amount ? ` · Refund ${fmtINR(li.refund_amount)}` : ''}${li.credit_note_amount ? ` · Credit note ${fmtINR(li.credit_note_amount)}` : ''}`}
                                  className="ml-1.5 align-middle text-xxs font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700"
                                >
                                  RETURNED
                                </span>
                              )}
                            </div>
                            {li.brand_name && <div className="text-xxs text-gray-500">{li.brand_name}</div>}
                            {li.returned && <div className="text-xxs text-orange-600">{li.return_reason || 'Returned'}</div>}
                          </>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-1 text-right">{li.qty}</td>
                      <td className="px-2 py-1 text-right">{fmtINR(li.sold_price)}</td>
                      <td className="px-2 py-1 text-right">{fmtINR(li.taxable_value)}</td>
                      <td className="px-2 py-1 text-right">{fmtINR(li.cost_pretax)}</td>
                      {isAdmin && <td className="px-2 py-1 text-right">{fmtINR(li.gross_margin)}</td>}
                      <td className="px-2 py-1 text-right">
                        <span className={`text-xxs font-medium px-1.5 py-0.5 rounded ${li.matched_zocs ? 'bg-zoddle-green/20 text-zoddle-green' : 'bg-gray-100 text-gray-600'}`}>
                          {li.matched_zocs ? 'ZOCS' : 'sheet'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReturnHistoryModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    listCustomerReturns()
      .then(d => setRows(d.returns || []))
      .catch(err => setError(err.response?.data?.detail || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Modal title="Return History" onClose={onClose} wide>
      {error && <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-xs text-zoddle-pink">{error}</div>}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No returns synced yet.</div>
      ) : (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-6 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium">Order</th>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Sold price</th>
                <th className="px-3 py-2 font-medium">Refund</th>
                <th className="px-3 py-2 font-medium">Credit note</th>
                <th className="px-6 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="px-6 py-2">
                    <div className="flex items-center gap-2">
                      {r.image_url_1 && <img src={cldThumb(r.image_url_1, 100)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">{r.design_name || r.zoddle_barcode}</p>
                        <p className="text-xs text-gray-400">{r.brand_name}{r.size ? ` · ${r.size}` : ''} · <span className="font-mono">{r.zoddle_barcode}</span></p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    <span className="font-mono text-xs">{r.appointment_uid || r.sheet_order_id}</span>
                    {r.sale_date && <p className="text-xs text-gray-400">{r.sale_date.slice(0, 10)}</p>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.customer_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">₹{r.sold_price}</td>
                  <td className="px-3 py-2 text-gray-600">{r.refund_amount != null ? `₹${r.refund_amount}` : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{r.credit_note_amount != null ? `₹${r.credit_note_amount}` : '—'}</td>
                  <td className="px-6 py-2 text-gray-600">{r.return_reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function WriteoffHistoryModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    listWriteoffs()
      .then(d => setRows(d.writeoffs || []))
      .catch(err => setError(err.response?.data?.detail || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Modal title="Gift & Non-Revenue History" onClose={onClose} wide>
      {error && <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-xs text-zoddle-pink">{error}</div>}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No gift or non-revenue orders synced yet.</div>
      ) : (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-6 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Order</th>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Value</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-6 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="px-6 py-2">
                    <div className="flex items-center gap-2">
                      {r.image_url_1 && <img src={cldThumb(r.image_url_1, 100)} alt="" className="w-8 h-8 rounded object-cover bg-gray-100 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">{r.design_name || r.zoddle_barcode}</p>
                        <p className="text-xs text-gray-400">{r.brand_name}{r.size ? ` · ${r.size}` : ''} · <span className="font-mono">{r.zoddle_barcode}</span></p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-600 capitalize">{r.record_type.replace('_', ' ')}</td>
                  <td className="px-3 py-2 text-gray-600">
                    <span className="font-mono text-xs">{r.appointment_uid || r.sheet_order_id}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.customer_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{r.value != null ? `₹${r.value}` : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {r.match_status === 'applied'
                      ? <span className="text-zoddle-green font-medium">Applied</span>
                      : <span className="text-zoddle-pink font-medium" title={r.match_status}>Needs attention</span>}
                  </td>
                  <td className="px-6 py-2 text-gray-600">{r.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function PastOrders() {
  const { auth } = useAuth();
  const isAdmin = auth?.access_level === 'admin';
  const zoddleSizes = useSizes();

  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState(new Set(['completed', 'rto', 'dol']));
  const [zoddleSize, setZoddleSize] = useState('');
  const [showColsMenu, setShowColsMenu] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (saved) {
      try { return new Set(JSON.parse(saved)); } catch { /* fall through */ }
    }
    return new Set(ALL_COLUMNS.filter(c => c.default).map(c => c.key));
  });

  useEffect(() => {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(visibleCols)));
  }, [visibleCols]);

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [resyncingUid, setResyncingUid] = useState(null);
  const [showReturnHistory, setShowReturnHistory] = useState(false);
  const [showWriteoffHistory, setShowWriteoffHistory] = useState(false);

  const filters = useMemo(() => {
    const { from, to } = computeDateRange(preset, customFrom, customTo);
    return {
      date_from:   fmtYMD(from),
      date_to:     fmtYMD(to),
      search:      search.trim() || undefined,
      statuses:    Array.from(statuses).join(',') || undefined,
      zoddle_size: zoddleSize || undefined,
      limit:       500,
    };
  }, [preset, customFrom, customTo, search, statuses, zoddleSize]);

  const reload = useCallback(() => {
    setLoading(true);
    setSummaryLoading(true);
    setError(null);
    listPastOrders(filters)
      .then(d => { setOrders(d.orders); setTotal(d.total); })
      .catch(err => setError(err.response?.data?.detail || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
    pastOrdersSummary(filters)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [filters]);

  useEffect(() => { reload(); }, [reload]);

  const onSyncNew = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await syncNew();
      // Response shape since 028b94a (+ returns leg added alongside customer returns): { sync: {...}, inventory: {...}, returns: {...} }
      const s = r.sync || r;
      const inv = r.inventory || {};
      const ret = r.returns || {};
      const msg = `Sync new: created ${s.orders_created || 0} orders, ${s.rto_dol_created || 0} RTO/DOL entries. Inventory: ${inv.marked_sold || 0} marked sold. Returns: ${ret.moved_to_restocking || 0} moved to restocking.`;
      window.alert(msg);
      reload();
    } catch (err) {
      window.alert('Sync failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const onResync = async (uid) => {
    if (!uid || resyncingUid) return;
    setResyncingUid(uid);
    try {
      await resyncByUid(uid);
      reload();
    } catch (err) {
      window.alert('Resync failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResyncingUid(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Past Orders</h1>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={onSyncNew}
              disabled={syncing}
              className="px-3 py-1.5 text-sm bg-zoddle-blue text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {syncing ? 'Syncing…' : 'Sync new'}
            </button>
            <button
              onClick={() => setShowReturnHistory(true)}
              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Return History
            </button>
            <button
              onClick={() => setShowWriteoffHistory(true)}
              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Gift & Non-Revenue
            </button>
          </div>
        )}
      </div>

      {showReturnHistory && <ReturnHistoryModal onClose={() => setShowReturnHistory(false)} />}
      {showWriteoffHistory && <WriteoffHistoryModal onClose={() => setShowWriteoffHistory(false)} />}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-4 py-1.5 text-sm rounded-full border transition ${
                preset === p.key
                  ? 'bg-zoddle-green text-white border-zoddle-green'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <div className="flex items-center gap-2 text-sm ml-2">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                     className="border border-gray-300 rounded px-2 py-1" />
              <span className="text-gray-500">to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                     className="border border-gray-300 rounded px-2 py-1" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <input
            type="text"
            placeholder="Search name or phone…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setSearch(searchInput); }}
            className="flex-1 min-w-[260px] border border-gray-300 rounded px-3 py-1.5"
          />
          <button
            onClick={() => setSearch(searchInput)}
            className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
          >
            Search
          </button>
          <div className="flex items-center gap-3">
            {STATUSES.map(s => {
              const on = statuses.has(s.key);
              return (
                <label key={s.key} className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => {
                      const next = new Set(statuses);
                      if (on) next.delete(s.key); else next.add(s.key);
                      setStatuses(next);
                    }}
                    className="accent-zoddle-blue"
                  />
                  {s.label}
                </label>
              );
            })}
          </div>
          <select
            value={zoddleSize}
            onChange={e => setZoddleSize(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All sizes</option>
            {zoddleSizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="ml-auto relative">
            <button
              onClick={() => setShowColsMenu(v => !v)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Columns ▾
            </button>
            {showColsMenu && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded shadow-lg z-20 p-2">
                {ALL_COLUMNS.filter(c => !c.adminOnly || isAdmin).map(c => (
                  <label key={c.key} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={visibleCols.has(c.key)}
                      onChange={() => {
                        const next = new Set(visibleCols);
                        if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
                        setVisibleCols(next);
                      }}
                    />
                    <span className="text-gray-700">{c.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <SummaryStrip summary={summary} loading={summaryLoading} isAdmin={isAdmin} />

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded mt-4 overflow-x-auto">
        {error && <div className="p-4 text-zoddle-pink text-sm">{error}</div>}
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {visibleCols.has('date')             && <th className="px-3 py-2 text-left">Date</th>}
                {visibleCols.has('customer')         && <th className="px-3 py-2 text-left">Customer</th>}
                {visibleCols.has('area')             && <th className="px-3 py-2 text-left">Area / Pincode</th>}
                {visibleCols.has('children')         && <th className="px-3 py-2 text-left">Children</th>}
                {visibleCols.has('executive')        && <th className="px-3 py-2 text-left">Executive</th>}
                {visibleCols.has('source')           && <th className="px-3 py-2 text-left">Source</th>}
                {visibleCols.has('status')           && <th className="px-3 py-2 text-left">Status</th>}
                {visibleCols.has('items_dispatched') && <th className="px-3 py-2 text-right">Items dispatched</th>}
                {visibleCols.has('avg_piece_age')    && <th className="px-3 py-2 text-right">Avg piece age</th>}
                {visibleCols.has('pack_rating')      && <th className="px-3 py-2 text-center">Pack rating</th>}
                {visibleCols.has('items')            && <th className="px-3 py-2 text-right">Items sold</th>}
                {visibleCols.has('brands_sold')      && <th className="px-3 py-2 text-left">Brands sold</th>}
                {visibleCols.has('pre_discount')     && <th className="px-3 py-2 text-right">Pre-discount</th>}
                {visibleCols.has('gmv')              && <th className="px-3 py-2 text-right">GMV</th>}
                {visibleCols.has('revenue')          && <th className="px-3 py-2 text-right">Revenue</th>}
                {visibleCols.has('discount_pct')     && <th className="px-3 py-2 text-right">Discount %</th>}
                {visibleCols.has('discount_code')    && <th className="px-3 py-2 text-left">Discount code</th>}
                {visibleCols.has('payment')          && <th className="px-3 py-2 text-left">Payment</th>}
                {isAdmin && visibleCols.has('margin') && <th className="px-3 py-2 text-right">Margin (% GMV)</th>}
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={20} className="px-3 py-8 text-center text-gray-400">No orders</td></tr>
              )}
              {orders.map(o => {
                const discPct = o.discount_rate != null
                  ? (Number(o.discount_rate) * 100).toFixed(1) + '%'
                  : (o.total_pre_disc && Number(o.total_pre_disc) > 0
                    ? (((Number(o.total_pre_disc) - Number(o.gmv)) / Number(o.total_pre_disc)) * 100).toFixed(1) + '%'
                    : '—');
                return (
                <tr key={o.sheet_order_id} className="border-t border-gray-100 hover:bg-gray-50">
                  {visibleCols.has('date') && <td className="px-3 py-2">{fmtDate(o.sale_date)}</td>}
                  {visibleCols.has('customer') && (
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{o.customer_name || '—'}</div>
                      <div className="text-xs text-gray-500">{o.customer_phone || '—'}</div>
                    </td>
                  )}
                  {visibleCols.has('area')             && <td className="px-3 py-2 text-sm text-gray-700">{o.area || '—'}</td>}
                  {visibleCols.has('children')         && <td className="px-3 py-2 text-xs text-gray-700">{o.children && o.children.length ? o.children.join(' · ') : '—'}</td>}
                  {visibleCols.has('executive')        && <td className="px-3 py-2 text-sm text-gray-700">{o.executive || '—'}</td>}
                  {visibleCols.has('source')           && <td className="px-3 py-2"><CategoryBadge category={o.category} /></td>}
                  {visibleCols.has('status')           && <td className="px-3 py-2"><StatusBadge status={o.status} /></td>}
                  {visibleCols.has('items_dispatched') && <td className="px-3 py-2 text-right">{o.items_dispatched != null ? o.items_dispatched : '—'}</td>}
                  {visibleCols.has('avg_piece_age')    && <td className="px-3 py-2 text-right">{o.avg_piece_age != null ? o.avg_piece_age.toFixed(1) : '—'}</td>}
                  {visibleCols.has('pack_rating')      && <td className="px-3 py-2 text-center"><PackRating value={o.pack_rating} /></td>}
                  {visibleCols.has('items')            && <td className="px-3 py-2 text-right">{o.item_count}</td>}
                  {visibleCols.has('brands_sold')      && <td className="px-3 py-2 text-xs text-gray-700">{o.brands_sold && o.brands_sold.length ? o.brands_sold.join(', ') : '—'}</td>}
                  {visibleCols.has('pre_discount')     && <td className="px-3 py-2 text-right">{o.total_pre_disc != null ? fmtINR(o.total_pre_disc) : '—'}</td>}
                  {visibleCols.has('gmv')              && <td className="px-3 py-2 text-right">{fmtINR(o.gmv)}</td>}
                  {visibleCols.has('revenue')          && <td className="px-3 py-2 text-right">{fmtINR(o.revenue)}</td>}
                  {visibleCols.has('discount_pct')     && <td className="px-3 py-2 text-right">{discPct}</td>}
                  {visibleCols.has('discount_code')    && <td className="px-3 py-2 text-sm text-gray-700">{o.discount_code || '—'}</td>}
                  {visibleCols.has('payment')          && <td className="px-3 py-2 text-sm text-gray-700">{o.payment_method || '—'}</td>}
                  {isAdmin && visibleCols.has('margin') && <td className="px-3 py-2 text-right">{fmtMarginWithPct(o.gross_margin, o.gmv)}</td>}
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      className="text-xs text-zoddle-blue hover:underline"
                      onClick={() => setSelectedOrder(o)}
                    >
                      View
                    </button>
                    {isAdmin && o.appointment_uid && (
                      <button
                        className="text-xs text-gray-600 hover:underline disabled:opacity-50"
                        disabled={resyncingUid === o.appointment_uid}
                        onClick={() => onResync(o.appointment_uid)}
                      >
                        {resyncingUid === o.appointment_uid ? 'Resyncing…' : 'Resync'}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > orders.length && (
        <div className="mt-2 text-xs text-gray-500 text-right">
          Showing {orders.length} of {total} (increase limit to see more)
        </div>
      )}

      {selectedOrder && (
        <DetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} isAdmin={isAdmin} />
      )}
    </div>
  );
}
